import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type DragEvent
} from "react";
import { api } from "../api";
import type { FsEntry, PanelUser } from "../types";

type ListResult = {
  path: string;
  entries: FsEntry[];
};

const ARCHIVE_RE = /\.(zip|tar\.gz|tgz)$/i;
const EDITABLE_RE =
  /\.(txt|html?|css|js|json|md|php|xml|ya?ml|env|ini|conf|log|svg|csv|tsx?|jsx|vue|py|sh|sql|htaccess|htpasswd|toml|cfg|twig|scss|less|map)$/i;

function isEditableName(name: string): boolean {
  if (name === ".htaccess" || name === ".htpasswd" || name === ".env") return true;
  if (name.startsWith(".") && !name.includes(".", 1)) return true;
  return EDITABLE_RE.test(name);
}

function isArchiveName(name: string): boolean {
  return ARCHIVE_RE.test(name) || name.endsWith(".tar.gz");
}

function joinPath(base: string, name: string): string {
  if (!base || base === ".") return name;
  return `${base.replace(/\/$/, "")}/${name}`;
}

function parentPath(p: string): string {
  const parts = p.split("/").filter(Boolean);
  parts.pop();
  return parts.length ? parts.join("/") : ".";
}

function formatSize(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function iconFor(e: FsEntry): string {
  if (e.type === "dir") return "📁";
  if (isArchiveName(e.name)) return "📦";
  if (e.name === ".htaccess" || e.name.endsWith(".htaccess")) return "🔐";
  if (/\.(php|js|ts|py|sh)$/i.test(e.name)) return "📄";
  if (/\.(png|jpe?g|gif|webp|svg|ico)$/i.test(e.name)) return "🖼";
  if (/\.(html?|css|scss)$/i.test(e.name)) return "🌐";
  return "📃";
}

export function FilesPage({
  accounts,
  currentUser,
  onRefreshAccounts
}: {
  accounts: PanelUser[];
  currentUser: PanelUser;
  onRefreshAccounts: () => Promise<void>;
}) {
  const staff =
    currentUser.role === "superadmin" || currentUser.role === "admin";
  const provisioned = accounts.filter(
    (a) => a.role === "user" && a.provisionStatus === "ready"
  );
  const defaultUserId = staff
    ? provisioned[0]?.id || ""
    : currentUser.provisionStatus === "ready"
      ? currentUser.id
      : "";

  const [userId, setUserId] = useState(defaultUserId);
  const [path, setPath] = useState(".");
  const [entries, setEntries] = useState<FsEntry[]>([]);
  const [error, setError] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState<string[]>([]);
  const [editorPath, setEditorPath] = useState<string | null>(null);
  const [editorContent, setEditorContent] = useState("");
  const [editorDirty, setEditorDirty] = useState(false);
  const [searchQ, setSearchQ] = useState("");
  const [searchHits, setSearchHits] = useState<FsEntry[] | null>(null);
  const [mkdirName, setMkdirName] = useState("");
  const [newFileName, setNewFileName] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const [uploadPct, setUploadPct] = useState<number | null>(null);
  const [chmodMode, setChmodMode] = useState("644");
  const [showChmod, setShowChmod] = useState(false);
  const [showRename, setShowRename] = useState(false);
  const [renameTo, setRenameTo] = useState("");
  const fileInput = useRef<HTMLInputElement>(null);

  const selectedAccount = useMemo(
    () =>
      staff
        ? provisioned.find((a) => a.id === userId)
        : currentUser,
    [staff, provisioned, userId, currentUser]
  );

  const crumbs = useMemo(() => {
    if (!path || path === ".") return [{ label: "home", path: "." }];
    const parts = path.split("/").filter(Boolean);
    const out = [{ label: "home", path: "." }];
    let acc = "";
    for (const p of parts) {
      acc = acc ? `${acc}/${p}` : p;
      out.push({ label: p, path: acc });
    }
    return out;
  }, [path]);

  const allSelected =
    entries.length > 0 && selected.length === entries.length;

  const load = useCallback(async () => {
    if (!userId) return;
    setBusy(true);
    setError("");
    try {
      const res = await api<ListResult & { ok: boolean }>(
        `/api/v1/files/list?userId=${encodeURIComponent(userId)}&path=${encodeURIComponent(path)}`
      );
      setEntries(res.entries || []);
      setSelected([]);
      setSearchHits(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }, [userId, path]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!userId && provisioned[0]?.id) setUserId(provisioned[0].id);
  }, [provisioned, userId]);

  const openEditor = async (p: string) => {
    setError("");
    setMsg("");
    try {
      const res = await api<{ content: string; path: string }>(
        `/api/v1/files/read?userId=${encodeURIComponent(userId)}&path=${encodeURIComponent(p)}`
      );
      setEditorPath(res.path);
      setEditorContent(res.content);
      setEditorDirty(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const saveEditor = async () => {
    if (!editorPath) return;
    setBusy(true);
    setError("");
    try {
      await api("/api/v1/files/write", {
        method: "POST",
        body: JSON.stringify({
          userId,
          path: editorPath,
          content: editorContent
        })
      });
      setEditorDirty(false);
      setMsg(`Saved ${editorPath}`);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const closeEditor = () => {
    if (editorDirty && !confirm("Discard unsaved changes?")) return;
    setEditorPath(null);
    setEditorDirty(false);
  };

  const doMkdir = async (e: FormEvent) => {
    e.preventDefault();
    if (!mkdirName.trim()) return;
    setError("");
    try {
      const p = joinPath(path, mkdirName.trim());
      await api("/api/v1/files/mkdir", {
        method: "POST",
        body: JSON.stringify({ userId, path: p })
      });
      setMkdirName("");
      setMsg(`Folder created: ${p}`);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const doNewFile = async (e: FormEvent) => {
    e.preventDefault();
    if (!newFileName.trim()) return;
    setError("");
    try {
      const p = joinPath(path, newFileName.trim());
      await api("/api/v1/files/write", {
        method: "POST",
        body: JSON.stringify({ userId, path: p, content: "" })
      });
      setNewFileName("");
      await openEditor(p);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const createHtaccess = async () => {
    const p = joinPath(path, ".htaccess");
    setError("");
    try {
      await api("/api/v1/files/write", {
        method: "POST",
        body: JSON.stringify({
          userId,
          path: p,
          content:
            "# RealPanel .htaccess\n# RewriteEngine On\n# RewriteCond %{REQUEST_FILENAME} !-f\n# RewriteRule ^ index.php [L]\n"
        })
      });
      setMsg("Created .htaccess");
      await openEditor(p);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const doDelete = async () => {
    if (!selected.length) return;
    if (!confirm(`Delete ${selected.length} item(s)? This cannot be undone.`))
      return;
    setBusy(true);
    setError("");
    try {
      for (const p of selected) {
        await api("/api/v1/files/delete", {
          method: "POST",
          body: JSON.stringify({ userId, path: p })
        });
      }
      setMsg(`Deleted ${selected.length} item(s)`);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const doRename = async () => {
    if (selected.length !== 1 || !renameTo.trim()) return;
    const from = selected[0]!;
    const to = joinPath(parentPath(from), renameTo.trim());
    setError("");
    try {
      await api("/api/v1/files/rename", {
        method: "POST",
        body: JSON.stringify({ userId, from, to })
      });
      setShowRename(false);
      setRenameTo("");
      setMsg(`Renamed to ${renameTo.trim()}`);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const doChmod = async () => {
    if (!selected.length || !chmodMode.trim()) return;
    setError("");
    try {
      for (const p of selected) {
        await api("/api/v1/files/chmod", {
          method: "POST",
          body: JSON.stringify({ userId, path: p, mode: chmodMode.trim() })
        });
      }
      setShowChmod(false);
      setMsg(`Permissions set to ${chmodMode}`);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const doZip = async () => {
    if (!selected.length) return;
    setError("");
    try {
      const dest = joinPath(path, `archive-${Date.now()}.zip`);
      await api("/api/v1/files/compress", {
        method: "POST",
        body: JSON.stringify({ userId, paths: selected, dest })
      });
      setMsg(`Created ${dest}`);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const doExtract = async (archivePath?: string) => {
    const arch =
      archivePath ||
      (selected.length === 1 ? selected[0] : undefined);
    if (!arch || !isArchiveName(arch)) {
      setError("Select a single .zip or .tar.gz archive to extract");
      return;
    }
    setError("");
    setBusy(true);
    try {
      await api("/api/v1/files/extract", {
        method: "POST",
        body: JSON.stringify({
          userId,
          archive: arch,
          dest: path === "." ? dirnameOf(arch) : path
        })
      });
      setMsg(`Extracted ${arch} → ${path}`);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const doCopy = async () => {
    if (selected.length !== 1) {
      setError("Select one item to copy");
      return;
    }
    const from = selected[0]!;
    const base = from.split("/").pop() || from;
    const name = prompt("Copy as", `copy-of-${base}`);
    if (!name) return;
    const to = joinPath(path, name);
    setError("");
    try {
      await api("/api/v1/files/copy", {
        method: "POST",
        body: JSON.stringify({ userId, from, to })
      });
      setMsg(`Copied to ${to}`);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const doSearch = async (e: FormEvent) => {
    e.preventDefault();
    if (!searchQ.trim()) return;
    setError("");
    try {
      const res = await api<{ matches: FsEntry[] }>(
        `/api/v1/files/search?userId=${encodeURIComponent(userId)}&query=${encodeURIComponent(searchQ)}&path=${encodeURIComponent(path)}`
      );
      setSearchHits(res.matches || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const fileToBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = String(reader.result || "");
        const i = result.indexOf(",");
        resolve(i >= 0 ? result.slice(i + 1) : result);
      };
      reader.onerror = () => reject(reader.error || new Error("read failed"));
      reader.readAsDataURL(file);
    });

  const onUpload = async (files: FileList | File[] | null) => {
    if (!files || (Array.isArray(files) ? !files.length : !files.length))
      return;
    const list = Array.from(files as FileList);
    setBusy(true);
    setError("");
    setMsg("");
    setUploadPct(0);
    try {
      let i = 0;
      for (const file of list) {
        if (file.size > 100 * 1024 * 1024) {
          throw new Error(
            `${file.name} exceeds 100 MB upload limit`
          );
        }
        const b64 = await fileToBase64(file);
        const dest = joinPath(path, file.name);
        await api("/api/v1/files/upload", {
          method: "POST",
          body: JSON.stringify({
            userId,
            path: dest,
            contentBase64: b64
          })
        });
        i += 1;
        setUploadPct(Math.round((i / list.length) * 100));
      }
      setMsg(`Uploaded ${list.length} file(s)`);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
      setUploadPct(null);
      if (fileInput.current) fileInput.current.value = "";
    }
  };

  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    void onUpload(e.dataTransfer.files);
  };

  const download = async (p: string) => {
    setError("");
    try {
      const res = await api<{
        contentBase64: string;
        name: string;
      }>(
        `/api/v1/files/download?userId=${encodeURIComponent(userId)}&path=${encodeURIComponent(p)}`
      );
      const bin = atob(res.contentBase64);
      const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
      const blob = new Blob([bytes]);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = res.name;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const toggleSelect = (p: string) => {
    setSelected((prev) =>
      prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p]
    );
  };

  const toggleSelectAll = () => {
    if (allSelected) setSelected([]);
    else setSelected(entries.map((e) => e.path));
  };

  const openItem = (e: FsEntry) => {
    if (e.type === "dir") setPath(e.path);
    else if (isEditableName(e.name)) void openEditor(e.path);
    else void download(e.path);
  };

  if (!userId) {
    return (
      <div className="fm2-page">
        <div className="fm2-hero">
          <div>
            <h1>File Manager</h1>
            <p>Provision an end-user account first.</p>
          </div>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => void onRefreshAccounts()}
          >
            Refresh
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fm2-page">
      <div className="fm2-hero">
        <div>
          <h1>File Manager</h1>
          <p>
            Full account file tools
            {selectedAccount?.username
              ? ` · ${selectedAccount.username}`
              : ""}
            {" · "}
            uploads up to 100&nbsp;MB
          </p>
        </div>
        <div className="fm2-hero-actions">
          {staff ? (
            <select
              className="inline-select"
              value={userId}
              onChange={(e) => {
                setUserId(e.target.value);
                setPath(".");
              }}
              aria-label="Account"
            >
              {provisioned.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.username}
                </option>
              ))}
            </select>
          ) : null}
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => void load()}
            disabled={busy}
          >
            Refresh
          </button>
        </div>
      </div>

      {msg ? <div className="success-banner">{msg}</div> : null}
      {error ? <div className="error-banner">{error}</div> : null}
      {uploadPct != null ? (
        <div className="fm2-progress">
          <div className="fm2-progress-bar" style={{ width: `${uploadPct}%` }} />
          <span>Uploading… {uploadPct}%</span>
        </div>
      ) : null}

      <div className="fm2-layout">
        <div className="fm2-main">
          {/* Path + toolbar */}
          <div className="fm2-card">
            <div className="fm2-pathbar">
              <div className="fm2-crumbs">
                {crumbs.map((c, i) => (
                  <button
                    key={c.path}
                    type="button"
                    className="fm2-crumb"
                    onClick={() => setPath(c.path)}
                  >
                    {i > 0 ? <span className="fm2-crumb-sep">/</span> : null}
                    {c.label}
                  </button>
                ))}
              </div>
              <div className="fm2-path-actions">
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  disabled={path === "."}
                  onClick={() => setPath(parentPath(path))}
                >
                  ↑ Up
                </button>
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={() => fileInput.current?.click()}
                  disabled={busy}
                >
                  Upload
                </button>
                <input
                  ref={fileInput}
                  type="file"
                  multiple
                  hidden
                  onChange={(e) => void onUpload(e.target.files)}
                />

              </div>
            </div>

            {/* Action toolbar */}
            <div className="fm2-toolbar">
              <button
                type="button"
                className="fm2-tool"
                disabled={!selected.length || busy}
                onClick={() => void doDelete()}
                title="Delete selected"
              >
                🗑 Delete
              </button>
              <button
                type="button"
                className="fm2-tool"
                disabled={selected.length !== 1}
                onClick={() => {
                  const base =
                    selected[0]?.split("/").pop() || selected[0] || "";
                  setRenameTo(base);
                  setShowRename(true);
                }}
              >
                ✎ Rename
              </button>
              <button
                type="button"
                className="fm2-tool"
                disabled={!selected.length}
                onClick={() => {
                  setChmodMode(
                    selected.length === 1 &&
                      entries.find((x) => x.path === selected[0])?.type ===
                        "dir"
                      ? "755"
                      : "644"
                  );
                  setShowChmod(true);
                }}
              >
                # Permissions
              </button>
              <button
                type="button"
                className="fm2-tool"
                disabled={selected.length !== 1}
                onClick={() => void doCopy()}
              >
                ⎘ Copy
              </button>
              <button
                type="button"
                className="fm2-tool"
                disabled={!selected.length}
                onClick={() => void doZip()}
              >
                📦 Compress
              </button>
              <button
                type="button"
                className="fm2-tool"
                disabled={
                  selected.length !== 1 ||
                  !selected[0] ||
                  !isArchiveName(selected[0])
                }
                onClick={() => void doExtract()}
              >
                📂 Extract
              </button>
              <button
                type="button"
                className="fm2-tool"
                disabled={
                  selected.length !== 1 ||
                  entries.find((x) => x.path === selected[0])?.type === "dir"
                }
                onClick={() => {
                  const e = entries.find((x) => x.path === selected[0]);
                  if (e && e.type === "file") void openEditor(e.path);
                }}
              >
                ✏ Edit
              </button>
              <button
                type="button"
                className="fm2-tool"
                onClick={() => void createHtaccess()}
              >
                🔐 .htaccess
              </button>
              <button
                type="button"
                className="fm2-tool"
                disabled={
                  selected.length !== 1 ||
                  entries.find((x) => x.path === selected[0])?.type !== "file"
                }
                onClick={() => selected[0] && void download(selected[0])}
              >
                ⬇ Download
              </button>
            </div>

            {/* Quick create + search */}
            <div className="fm2-quick">
              <form onSubmit={doMkdir} className="fm2-quick-form">
                <input
                  placeholder="New folder name"
                  value={mkdirName}
                  onChange={(e) => setMkdirName(e.target.value)}
                />
                <button type="submit" className="btn btn-ghost btn-sm">
                  + Folder
                </button>
              </form>
              <form onSubmit={doNewFile} className="fm2-quick-form">
                <input
                  placeholder="New file (e.g. index.php)"
                  value={newFileName}
                  onChange={(e) => setNewFileName(e.target.value)}
                />
                <button type="submit" className="btn btn-ghost btn-sm">
                  + File
                </button>
              </form>
              <form onSubmit={doSearch} className="fm2-quick-form grow">
                <input
                  placeholder="Search filenames…"
                  value={searchQ}
                  onChange={(e) => setSearchQ(e.target.value)}
                />
                <button type="submit" className="btn btn-ghost btn-sm">
                  Search
                </button>
              </form>
            </div>

            {searchHits ? (
              <div className="fm2-search-hits">
                <div className="fm2-search-head">
                  <strong>Search results ({searchHits.length})</strong>
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={() => setSearchHits(null)}
                  >
                    Clear
                  </button>
                </div>
                <ul>
                  {searchHits.map((h) => (
                    <li key={h.path}>
                      <button
                        type="button"
                        className="fm2-hit"
                        onClick={() => {
                          if (h.type === "dir") setPath(h.path);
                          else void openEditor(h.path);
                        }}
                      >
                        {iconFor(h)} {h.path}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            {/* Drop zone + table */}
            <div
              className={`fm2-drop ${dragOver ? "over" : ""}`}
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={onDrop}
            >
              {dragOver ? (
                <div className="fm2-drop-hint">Drop files to upload here</div>
              ) : null}
              <div className="fm2-table-wrap">
                <table className="fm2-table">
                  <thead>
                    <tr>
                      <th className="fm2-check">
                        <input
                          type="checkbox"
                          checked={allSelected}
                          onChange={toggleSelectAll}
                          disabled={!entries.length}
                          aria-label="Select all"
                        />
                      </th>
                      <th>Name</th>
                      <th>Size</th>
                      <th>Permissions</th>
                      <th>Modified</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {path !== "." ? (
                      <tr className="fm2-up">
                        <td />
                        <td colSpan={5}>
                          <button
                            type="button"
                            className="fm2-name-btn"
                            onClick={() => setPath(parentPath(path))}
                          >
                            📁 ..
                          </button>
                        </td>
                      </tr>
                    ) : null}
                    {entries.map((e) => {
                      const sel = selected.includes(e.path);
                      return (
                        <tr
                          key={e.path}
                          className={sel ? "selected" : ""}
                          onDoubleClick={() => openItem(e)}
                        >
                          <td className="fm2-check">
                            <input
                              type="checkbox"
                              checked={sel}
                              onChange={() => toggleSelect(e.path)}
                              aria-label={`Select ${e.name}`}
                            />
                          </td>
                          <td>
                            <button
                              type="button"
                              className="fm2-name-btn"
                              onClick={() => openItem(e)}
                            >
                              <span className="fm2-ico">{iconFor(e)}</span>
                              <span className={e.type === "dir" ? "dir" : ""}>
                                {e.name}
                              </span>
                            </button>
                          </td>
                          <td className="mono faint">
                            {e.type === "file" ? formatSize(e.size) : "—"}
                          </td>
                          <td className="mono faint">{e.mode}</td>
                          <td className="mono faint">
                            {new Date(e.mtime).toLocaleString()}
                          </td>
                          <td>
                            <div className="fm2-row-acts">
                              {e.type === "file" && isEditableName(e.name) ? (
                                <button
                                  type="button"
                                  className="btn btn-ghost btn-sm"
                                  onClick={() => void openEditor(e.path)}
                                >
                                  Edit
                                </button>
                              ) : null}
                              {e.type === "file" && isArchiveName(e.name) ? (
                                <button
                                  type="button"
                                  className="btn btn-ghost btn-sm"
                                  onClick={() => void doExtract(e.path)}
                                >
                                  Extract
                                </button>
                              ) : null}
                              {e.type === "file" ? (
                                <button
                                  type="button"
                                  className="btn btn-ghost btn-sm"
                                  onClick={() => void download(e.path)}
                                >
                                  ↓
                                </button>
                              ) : null}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                    {entries.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="fm2-empty">
                          Empty folder — upload files or create a new folder
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
              <p className="fm2-hint">
                Tip: drag &amp; drop files onto the list to upload. Double-click a
                file to edit (or download binaries). Select archives and use
                Extract for zip / tar.gz.
              </p>
            </div>
          </div>
        </div>

      </div>

      {/* Rename modal */}
      {showRename ? (
        <div className="fm2-modal-bg" onClick={() => setShowRename(false)}>
          <div
            className="fm2-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
          >
            <h3>Rename</h3>
            <input
              className="fm2-modal-input"
              value={renameTo}
              onChange={(e) => setRenameTo(e.target.value)}
              autoFocus
            />
            <div className="fm2-modal-acts">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => setShowRename(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => void doRename()}
              >
                Rename
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* Chmod modal */}
      {showChmod ? (
        <div className="fm2-modal-bg" onClick={() => setShowChmod(false)}>
          <div
            className="fm2-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
          >
            <h3>Permissions ({selected.length} item(s))</h3>
            <input
              className="fm2-modal-input mono"
              value={chmodMode}
              onChange={(e) => setChmodMode(e.target.value)}
              placeholder="644 or 755"
              autoFocus
            />
            <div className="fm2-chmod-presets">
              {["644", "664", "600", "755", "775", "700"].map((m) => (
                <button
                  key={m}
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={() => setChmodMode(m)}
                >
                  {m}
                </button>
              ))}
            </div>
            <div className="fm2-modal-acts">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => setShowChmod(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => void doChmod()}
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* Code editor */}
      {editorPath ? (
        <div className="fm2-editor-bg">
          <div className="fm2-editor">
            <div className="fm2-editor-head">
              <div>
                <strong className="mono">{editorPath}</strong>
                {editorDirty ? (
                  <span className="fm2-dirty"> · unsaved</span>
                ) : null}
                {editorPath.endsWith(".htaccess") ||
                editorPath.endsWith("/.htaccess") ||
                editorPath === ".htaccess" ? (
                  <span className="fm2-htaccess-badge">.htaccess editor</span>
                ) : null}
              </div>
              <div className="topbar-actions">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => void saveEditor()}
                  disabled={busy || !editorDirty}
                >
                  Save
                </button>
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={closeEditor}
                >
                  Close
                </button>
              </div>
            </div>
            <textarea
              className="fm2-editor-area mono"
              value={editorContent}
              onChange={(e) => {
                setEditorContent(e.target.value);
                setEditorDirty(true);
              }}
              spellCheck={false}
            />
          </div>
        </div>
      ) : null}
    </div>
  );
}

function dirnameOf(p: string): string {
  const i = p.lastIndexOf("/");
  if (i <= 0) return ".";
  return p.slice(0, i) || ".";
}
